import { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ratingsApi } from '../api/ratings';
import { useAuthStore } from '../store/authStore';
import { tokens } from '../theme';

// The five rating categories, in display order. Rendered as a flat
// horizontal strip beneath the hero — label-only, no icons or copy.
const WHAT_WE_RATE = [
  'Payment Reliability',
  'Communication',
  'Fair Play',
  'League Commitment',
  'Fair Trader',
];

/**
 * Homepage layout:
 *   - Full-bleed hero: football video (plays once, freezes on the last
 *     frame), bottom-left editorial content block with the primary CTA,
 *     and a floating Top Reps rankings panel on the bottom-right
 *   - "What's Rated" horizontal strip immediately beneath the hero: a
 *     simple label-only list of the five rating categories
 *
 * Layout.tsx is aware of the "/" route and renders the AppBar with a
 * transparent/gradient background so the video shows through.
 */
export default function HomePage() {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Slow the video down a little for a more cinematic feel. It's set to
  // play ONCE (no `loop` prop) and then stays on the final frame until
  // the user refreshes the page.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onLoaded = () => {
      v.playbackRate = 0.65;
    };
    v.addEventListener('loadedmetadata', onLoaded);
    if (v.readyState >= 1) v.playbackRate = 0.65;
    return () => v.removeEventListener('loadedmetadata', onLoaded);
  }, []);

  // When the video finishes, pin it to just before the end. Native HTML5
  // already shows the last frame once playback stops, but explicitly
  // pausing (and rewinding a tiny bit) guards against some browsers
  // showing a black frame or the controls overlay.
  const handleEnded = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    if (v.duration && Number.isFinite(v.duration)) {
      v.currentTime = Math.max(0, v.duration - 0.05);
    }
  };

  const { data: topRated } = useQuery({
    queryKey: ['topRated', 5],
    queryFn: () => ratingsApi.getTopRated(5),
    staleTime: 5 * 60 * 1000,
  });

  // Top Avoids — worst-rated managers (overall_average <= 2.5 server-side).
  // The /top-rated endpoint defaults to >= 3.5 so the two lists can never
  // share a user, even with very few reviews in the system.
  const { data: topAvoids } = useQuery({
    queryKey: ['topAvoids', 5],
    queryFn: () => ratingsApi.getTopAvoids(5),
    staleTime: 5 * 60 * 1000,
  });

  // Hero CTA adapts to auth state. Three tiers, each with its own
  // color + matching glow halo so the button reads as a distinct
  // call-to-action depending on where the viewer is in the funnel:
  //   - Visitor (no account): brand orange "Create Your Rep Account"
  //   - Logged in but unverified: amber-warning "Verify Your Account"
  //     → /dashboard?verify=1 (auto-opens the link/verify dialog)
  //   - Verified: gold (trust-tier) "Rate Your Leaguemates"
  //     → /leaguemates
  //
  // `glowHex` is the literal hex used to compose the drop-shadow
  // halo and the keyframe animation (MUI palette tokens can't be
  // composed into the filter string).
  const { isAuthenticated, user } = useAuthStore();
  const heroCta = (() => {
    if (!isAuthenticated) {
      return {
        label: 'Create Your Rep Account',
        to: '/register',
        color: 'primary' as const,
        glowHex: tokens.orange,
      };
    }
    if (!user?.isVerified) {
      return {
        label: 'Verify Your Account',
        to: '/dashboard?verify=1',
        color: 'warning' as const,
        glowHex: '#f0b942', // matches theme.palette.warning.main
      };
    }
    return {
      label: 'Rate Your Leaguemates',
      to: '/leaguemates',
      color: 'secondary' as const,
      glowHex: tokens.gold,
    };
  })();

  return (
    <Box sx={{ bgcolor: tokens.bg }}>
      {/* ─── HERO ─── */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          minHeight: 700,
          overflow: 'hidden',
        }}
      >
        <Box
          component="video"
          ref={videoRef}
          autoPlay
          muted
          playsInline
          onEnded={handleEnded}
          src="/hero/hero.mp4"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 20%',
          }}
        />

        {/* Layered gradient overlays: bottom darker for readability, left
            darker to keep the headline grounded. */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'linear-gradient(to top, rgba(10,10,12,1) 0%, rgba(10,10,12,0.6) 30%, rgba(10,10,12,0.1) 60%, transparent 100%),' +
              'linear-gradient(to right, rgba(10,10,12,0.6) 0%, transparent 50%)',
          }}
        />

        {/* Hero content (bottom-left) */}
        <Box
          className="fade-up"
          sx={{
            position: 'absolute',
            bottom: { xs: 40, md: 60 },
            left: { xs: 20, md: 52 },
            right: { xs: 20, md: 'auto' },
            zIndex: 10,
            maxWidth: 580,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              color: tokens.orange,
              mb: 1.5,
              fontFamily: '"DM Sans", sans-serif',
              '&::before': {
                content: '""',
                display: 'block',
                width: 28,
                height: 2,
                backgroundColor: tokens.orange,
              },
            }}
          >
            Dynasty Fantasy Football
          </Box>

          <Typography
            component="h1"
            sx={{
              fontFamily: '"Barlow Condensed", sans-serif',
              fontWeight: 900,
              fontSize: { xs: 60, sm: 80, md: 100 },
              lineHeight: 0.92,
              letterSpacing: '-1px',
              textTransform: 'uppercase',
              color: tokens.text,
              mb: 2.5,
            }}
          >
            Your Rep.
            <br />
            Your{' '}
            <Box component="em" sx={{ fontStyle: 'normal', color: tokens.orange }}>
              Legacy.
            </Box>
          </Typography>

          <Typography
            sx={{
              fontSize: 16,
              color: 'rgba(240,236,227,0.62)',
              lineHeight: 1.55,
              maxWidth: 400,
              mb: 3.75,
            }}
          >
            The community-powered reputation platform for dynasty fantasy football managers.
            Trade smarter. Play better. Own your legacy.
          </Typography>

          <Button
            component={RouterLink}
            to={heroCta.to}
            size="large"
            variant="contained"
            color={heroCta.color}
            sx={{
              fontSize: 17,
              letterSpacing: '1.5px',
              padding: '14px 32px',
              clipPath:
                'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
              // Soft pulsing halo in the CTA's accent color. Using
              // `filter: drop-shadow(...)` instead of `box-shadow`
              // because drop-shadow follows the actual rendered
              // shape (including the clip-path notches), where
              // box-shadow would draw a rectangular halo and look
              // wrong. Two stacked shadows give it depth.
              filter: `drop-shadow(0 0 12px ${heroCta.glowHex}99) drop-shadow(0 0 28px ${heroCta.glowHex}55)`,
              animation: 'dynastyrep-hero-cta-pulse 2.6s ease-in-out infinite',
              '@keyframes dynastyrep-hero-cta-pulse': {
                '0%, 100%': {
                  filter: `drop-shadow(0 0 12px ${heroCta.glowHex}99) drop-shadow(0 0 28px ${heroCta.glowHex}55)`,
                  transform: 'translateZ(0) scale(1)',
                },
                '50%': {
                  filter: `drop-shadow(0 0 18px ${heroCta.glowHex}cc) drop-shadow(0 0 42px ${heroCta.glowHex}88)`,
                  transform: 'translateZ(0) scale(1.015)',
                },
              },
              // Respect users who've asked the OS not to animate UI.
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                filter: `drop-shadow(0 0 14px ${heroCta.glowHex}aa) drop-shadow(0 0 32px ${heroCta.glowHex}66)`,
              },
              // On hover, brighten the halo so the button feels
              // tactile when the cursor lands on it.
              '&:hover': {
                filter: `drop-shadow(0 0 20px ${heroCta.glowHex}ee) drop-shadow(0 0 48px ${heroCta.glowHex}99)`,
              },
            }}
          >
            {heroCta.label}
          </Button>
        </Box>

        {/* Floating rankings — two distinct panels stacked with a gap
            so the gold-tier Reps and the warning-tier Avoids read as
            separate things rather than one long mixed leaderboard.
            Mutually exclusive on the server (Top Reps >= 3.5,
            Top Avoids <= 2.5) so a manager can't appear on both.
            Hidden on mobile — the hero CTA takes precedence there. */}
        <Box
          className="fade-left"
          sx={{
            position: 'absolute',
            bottom: 48,
            right: 44,
            zIndex: 20,
            width: 300,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* ─── Top Reps panel ─── */}
          <Box
            sx={{
              background: 'rgba(12,12,18,0.82)',
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(20px) saturate(1.4)',
              WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px 12px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Barlow Condensed", sans-serif',
                  fontWeight: 800,
                  fontSize: 16,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: tokens.text,
                }}
              >
                Top <Box component="span" sx={{ color: tokens.orange }}>Reps</Box>
              </Typography>
              <Box
                component={RouterLink}
                to="/top-rated"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: tokens.orange,
                  textDecoration: 'none',
                  transition: 'color 0.15s',
                  '&:hover': { color: tokens.orange2 },
                }}
              >
                Full Rankings →
              </Box>
            </Box>

            {topRated && topRated.length > 0 ? (
              topRated.slice(0, 5).map((u, idx) => (
                <RankingRow
                  key={u.sleeper_account_id}
                  rank={idx + 1}
                  username={u.account.current_username}
                  displayName={u.account.current_display_name}
                  avatarId={u.account.current_avatar_id}
                  sleeperUserId={u.account.sleeper_user_id}
                  score={u.overall_average}
                  totalRatings={u.total_ratings}
                />
              ))
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{ color: tokens.text3, fontSize: 12 }}
                >
                  Be one of the first rated managers.
                </Typography>
              </Box>
            )}
          </Box>

          {/* ─── Top Avoids panel ─── */}
          <Box
            sx={{
              background: 'rgba(12,12,18,0.82)',
              // Subtle red-tinted border so the panel reads as a
              // warning at a glance even before you scan the rows.
              border: '1px solid rgba(224,107,90,0.22)',
              backdropFilter: 'blur(20px) saturate(1.4)',
              WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px 12px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Barlow Condensed", sans-serif',
                  fontWeight: 800,
                  fontSize: 16,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: tokens.text,
                }}
              >
                Top{' '}
                <Box component="span" sx={{ color: '#e06b5a' }}>
                  Avoids
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: tokens.text3,
                }}
              >
                ≤ 2.5 rating
              </Typography>
            </Box>

            {topAvoids && topAvoids.length > 0 ? (
            topAvoids.slice(0, 5).map((u, idx) => (
              <RankingRow
                key={u.sleeper_account_id}
                rank={idx + 1}
                username={u.account.current_username}
                displayName={u.account.current_display_name}
                avatarId={u.account.current_avatar_id}
                sleeperUserId={u.account.sleeper_user_id}
                score={u.overall_average}
                totalRatings={u.total_ratings}
                variant="avoid"
              />
            ))
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{ color: tokens.text3, fontSize: 12 }}
              >
                No managers in the cautionary tier yet.
              </Typography>
            </Box>
          )}
          </Box>
        </Box>
      </Box>

      {/* ─── WHAT'S RATED (label-only horizontal strip) ─── */}
      <Box
        component="section"
        sx={{
          borderTop: `1px solid ${tokens.border}`,
          borderBottom: `1px solid ${tokens.border}`,
          backgroundColor: tokens.bg,
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            py: { xs: 3, md: 3.5 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 2, md: 5 },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          {/* Section label */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexShrink: 0,
              '&::before': {
                content: '""',
                display: 'block',
                width: 28,
                height: 2,
                backgroundColor: tokens.orange,
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                color: tokens.orange,
              }}
            >
              What's Rated
            </Typography>
          </Box>

          {/* 5 category labels, separated by thin dividers on md+ */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 2, md: 0 },
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              flex: 1,
              justifyContent: { xs: 'flex-start', md: 'space-around' },
            }}
          >
            {WHAT_WE_RATE.map((label, i) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { md: 3 },
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Barlow Condensed", sans-serif',
                    fontWeight: 700,
                    fontSize: { xs: 15, md: 17 },
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    color: tokens.text,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Typography>
                {/* thin divider after every item except the last (md+ only) */}
                {i < WHAT_WE_RATE.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      width: 1,
                      height: 16,
                      backgroundColor: tokens.border2,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function RankingRow({
  rank,
  username,
  displayName,
  avatarId,
  sleeperUserId,
  score,
  totalRatings,
  variant = 'top',
}: {
  rank: number;
  username: string;
  displayName: string | null;
  avatarId: string | null;
  /** Sleeper user id used to link the row to the public profile page.
   *  The whole row becomes a router link so clicks anywhere on it
   *  navigate to /sleeper/:id. */
  sleeperUserId: string;
  score: number;
  totalRatings: number;
  /** 'top' uses gold/silver/bronze for the top three ranks (default).
   *  'avoid' uses a red/danger ramp so the cautionary list reads as a
   *  warning at a glance. */
  variant?: 'top' | 'avoid';
}) {
  const isTop = rank <= 3;
  const initials = username.slice(0, 2).toUpperCase();
  const filledStars = Math.round(score);

  // For "avoid" rank #1 = "worst offender", which deserves the most
  // alarming color. The ramp goes hot → cooler red as ranks descend.
  const rankColor =
    variant === 'avoid'
      ? rank === 1
        ? '#ff5252'
        : rank === 2
        ? '#e06b5a'
        : rank === 3
        ? '#b04c3e'
        : tokens.text3
      : rank === 1
      ? tokens.gold
      : rank === 2
      ? '#c0c0c0'
      : rank === 3
      ? '#b87333'
      : tokens.text3;

  return (
    <Box
      component={RouterLink}
      to={`/sleeper/${sleeperUserId}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        padding: '10px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        transition: 'background 0.15s',
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { background: 'rgba(255,255,255,0.04)' },
      }}
    >
      <Box
        sx={{
          fontFamily: '"Barlow Condensed", sans-serif',
          fontWeight: 800,
          fontSize: 18,
          color: isTop ? rankColor : tokens.text3,
          width: 22,
          flexShrink: 0,
          textAlign: 'center',
        }}
      >
        {rank}
      </Box>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: isTop ? `${rankColor}22` : tokens.border2,
          border: `1.5px solid ${isTop ? rankColor : tokens.border}`,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '"Barlow Condensed", sans-serif',
          fontWeight: 700,
          fontSize: 13,
          color: isTop ? rankColor : tokens.text2,
          overflow: 'hidden',
          backgroundImage: avatarId
            ? `url(https://sleepercdn.com/avatars/thumbs/${avatarId})`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {!avatarId && initials}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: tokens.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayName || username}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: tokens.text3,
            fontWeight: 400,
            mt: '1px',
          }}
        >
          {totalRatings} rating{totalRatings === 1 ? '' : 's'}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '2px',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Barlow Condensed", sans-serif',
            fontWeight: 700,
            fontSize: 17,
            color: tokens.text,
            lineHeight: 1,
          }}
        >
          {score.toFixed(1)}
        </Typography>
        <Box sx={{ display: 'flex', gap: '1px' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Box
              key={i}
              component="span"
              sx={{
                fontSize: 9,
                color: i < filledStars ? tokens.gold : tokens.border2,
              }}
            >
              ★
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
